import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';
import type { AttributeTypeAdmin } from './types';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

interface ResponseData {
  data: AttributeTypeAdmin[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * GET api/v1/cms/master-product-attributes/types
 * CMS management surface — includes inactive types/values and admin-only fields
 * (synonyms, isActive) that the read-only dictionary endpoint omits.
 * No staleTime: the attributes management screen always wants fresh data.
 */
export function useGetAttributeTypesApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  // Destructure to compose caller enabled with built-in guards (never replace)
  const { enabled: callerEnabled, ...restOptions } = options ?? {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${productAttributesEndpoint}/types`);

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([`${productAttributesEndpoint}/types`], queryFn, {
    enabled: (callerEnabled ?? true) && isLoggedIn,
    staleTime: 0,
    retry: false,
    ...restOptions,
  });
}
